import { ReactElement, useState } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { LoadingIcon } from "@/Common/Components/IconControls/LoadingIcon";
import { SelectControl } from "@/Common/Components/Inputs/SelectControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { useAvailableFlowsheetTemplates } from "@/Flowsheets/Hooks/useAvailableFlowsheetTemplates";
import { useGetFlowsheetName } from "@/Flowsheets/Hooks/useGetFlowsheetName";
import { FlowsheetTemplate } from "@/Flowsheets/Models/FlowsheetTemplate";

import styles from "./AddFlowsheetModal.module.scss";

/**
 * The properties for a {@link AddFlowsheetModal} component.
 */
interface IAddFlowsheetModalProps {

    /**
     * The patient id.
     */
    patientId: Guid;

    /**
     * The encounter id.
     */
    encounterId: Guid;

    /**
     * A callback invoked when the flowsheet template is submitted.
     * @param selectedFlowsheetTemplate The selected flowsheet template.
     */
    onSubmit: (selectedFlowsheetTemplate: FlowsheetTemplate) => void;

    /**
     * A callback invoked when the modal close button clicked.
     */
    onClose?: () => void;
}

/**
 * A modal that allows to select a flowsheet template to add a new flowsheet.
 * @param props The AddFlowsheetModal properties.
 */
function AddFlowsheetModal({ patientId, encounterId, onSubmit, onClose }: Readonly<IAddFlowsheetModalProps>): ReactElement {
    const { isLoading, isError, data: flowsheetTemplates, isSuccess } = useAvailableFlowsheetTemplates({ patientId, encounterId });
    const { getName } = useGetFlowsheetName();
    const [flowsheetTemplate, setFlowsheetTemplate] = useState<FlowsheetTemplate | null>(null);
    const { getText } = useTranslation();

    const options = flowsheetTemplates?.map(flowsheetTemplate => ({
        label: getName(flowsheetTemplate),
        value: flowsheetTemplate.Name
    })) ?? [];

    return (
        // Portal is required to prevent the parent from scrolling when focusing something in the modal
        createPortal(
            <Modal
                title={getText("Flowsheets_AddFlowsheetLabel")}
                dataCy="add-flowsheet-modal"
                showX
                disableAutoScroll
                footer={(
                    <div className={styles.addFlowsheetModalFooter}>
                        <ButtonGroup spacing="sm">
                            <ButtonControl
                                variant="neutral"
                                onClick={() => onClose?.()}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                dataCy="add-flowsheet-modal-continue-button"
                                onClick={() => onSubmit(flowsheetTemplate)}
                                disabled={!flowsheetTemplate}
                                label={cacheStore.GetString("ContinueLabel")}
                            />
                        </ButtonGroup>
                    </div>
                )}
                onClose={() => onClose?.()}
            >
                {isLoading && (
                    <div className={styles.loadingContainer}>
                        <LoadingIcon />
                    </div>
                )}
                {isError && <div>{getText("UnexpectedErrorMessage")}</div>}
                {isSuccess && (
                    <SelectControl
                        defaultMenuIsOpen={!flowsheetTemplate}
                        options={options}
                        onChange={value => setFlowsheetTemplate(flowsheetTemplates.find(x => x.Name === value))}
                        value={flowsheetTemplate ? flowsheetTemplate.Name : null}
                    />
                )}
            </Modal>,
            document.body
        )
    );
}

export { AddFlowsheetModal };
